import React, { useEffect, useState, useContext } from 'react';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  TextField,
  Stack,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Folder,
  ExpandMore,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import contribuenteApi from '../../api/contribuenteApi';
import { useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ProtectedAction from '../../components/ProtectedAction';
import BackButtonDesktop from '../../components/BackButtonDesktop';
import { AuthContext } from '../../auth/AuthContext';

export default function ContribuentiListPageMobile() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN';
  const isStudio = user?.role === 'STUDIO';

  const [contribuenti, setContribuenti] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [sortField, setSortField] = useState('cognome');
  const [sortDirection, setSortDirection] = useState('ASC');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    fetchContribuenti(0);
  }, [user]);

  const fetchContribuenti = (pageNumber) => {
    if (!user) return;
    setLoading(true);
    const params = {
      page: pageNumber,
      size: pageSize,
      sort: `${sortField},${sortDirection}`,
      ...filters,
    };

    const call =
      user.role === 'ADMIN'
        ? contribuenteApi.list(params)
        : contribuenteApi.listbystudio(user.studioId, params);

    call
      .then((r) => {
        setContribuenti(r.data.content || []);
        setTotalPages(r.data.totalPages || 0);
        setPage(r.data.number || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const openDeleteDialog = (id) => {
    setSelectedId(id);
    setOpenDeleteModal(true);
  };
  const closeDeleteDialog = () => {
    setSelectedId(null);
    setOpenDeleteModal(false);
  };
  const confirmDelete = async () => {
    try {
      await contribuenteApi.delete(selectedId);
      fetchContribuenti(page);
    } catch (err) {
      console.error(err);
    }
    closeDeleteDialog();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
    fetchContribuenti(0);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'ASC' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  const exportToCSV = async () => {
    try {
      const res =
        user.role === 'ADMIN'
          ? contribuenteApi.list({ page: 0, size: totalPages * pageSize || 1000 })
          : contribuenteApi.listbystudio(user.studioId, { page: 0, size: totalPages * pageSize || 1000 });
      const allContribuenti = (await res).data.content || [];

      const headers = ['Nome','Cognome','Codice Fiscale','Partita IVA','Email','Telefono','Indirizzo','Regime Fiscale','ID'];
      const rows = allContribuenti.map(c => [
        c.nome||'', c.cognome||'', c.codiceFiscale||'', c.partitaIVA||'', c.email||'',
        c.telefono||'', c.indirizzo||'', c.regimeFiscale||'', c.id
      ]);

      const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'contribuenti.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch(err){
      console.error('Errore esportazione CSV:', err);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt:2, pb:4 }}>
      {loading && (
        <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(255,255,255,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1300 }}>
          <CircularProgress size={48} />
        </Box>
      )}

      <BackButtonDesktop />
      <Typography variant="h5" sx={{ mb:2 }}>Contribuenti</Typography>

      {/* Filtri */}
      <Stack spacing={1} sx={{ mb:2 }}>
        <TextField
          label="Cognome"
          size="small"
          value={filters.cognome||''}
          onChange={(e)=>setFilters(prev=>({...prev,cognome:e.target.value}))}
        />
        <TextField
          label="Partita IVA"
          size="small"
          value={filters.partitaIva||''}
          onChange={(e)=>setFilters(prev=>({...prev,partitaIva:e.target.value}))}
        />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" fullWidth onClick={()=>fetchContribuenti(0)}>Cerca</Button>
          <Button variant="outlined" fullWidth onClick={()=>{setFilters({}); fetchContribuenti(0)}}>Reset</Button>
        </Stack>
      </Stack>

      {/* Azioni */}
      {(isAdmin||isStudio) &&
        <Stack direction="row" spacing={1} sx={{ mb:2 }} flexWrap="wrap">
          {isStudio &&
            <ProtectedAction user={user} contractState={user.statoContratto}>
              <Button variant="contained" fullWidth onClick={()=>navigate(`/contribuenti/createfromuser/${user.userId}`)}>
                Aggiungi Contribuente
              </Button>
            </ProtectedAction>
          }
          {isAdmin &&
            <Button variant="contained" fullWidth onClick={()=>navigate('/contribuenti/create')}>
              Inserisci Contribuente
            </Button>
          }
          <Button variant="outlined" fullWidth onClick={exportToCSV}>Esporta CSV</Button>
        </Stack>
      }
      {/* Paginazione */}
      <Stack direction="row" spacing={1} sx={{ mt:2 }} flexWrap="wrap">
        {Array.from({ length: totalPages }, (_, i)=>(
          <Button key={i} variant={i===page?'contained':'outlined'} size="small" onClick={()=>fetchContribuenti(i)}>{i+1}</Button>
        ))}
      </Stack>
      {/* Lista accordion */}
      {contribuenti.map(c => (
        <Accordion key={c.id} sx={{ mb:1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography sx={{ fontWeight:'bold' }}>{c.nome} {c.cognome}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.5}>
              <Typography><strong>Codice Fiscale:</strong> {c.codiceFiscale}</Typography>
              <Typography><strong>Partita IVA:</strong> {c.partitaIVA}</Typography>
              <Typography><strong>Email:</strong> {c.email}</Typography>
              <Typography><strong>Telefono:</strong> {c.telefono}</Typography>
              <Typography><strong>Indirizzo:</strong> {c.indirizzo}</Typography>
              <Typography><strong>Regime Fiscale:</strong> {c.regimeFiscale}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ mt:1 }} flexWrap="wrap">
              <Tooltip title="Dettaglio">
                <IconButton color="primary" size="small" onClick={()=>navigate(`/contribuenti/${c.id}`)}><Visibility/></IconButton>
              </Tooltip>
              <Tooltip title="Fascicolo">
                <IconButton color="primary" size="small" onClick={()=>navigate(`/contribuente/${c.id}/fascicolo`)}><Folder/></IconButton>
              </Tooltip>
              <Tooltip title="Modifica">
                <IconButton color="secondary" size="small" onClick={()=>navigate(`/contribuenti/edit/${c.id}`)}><Edit/></IconButton>
              </Tooltip>
              <Tooltip title="Elimina">
                <IconButton color="error" size="small" onClick={()=>openDeleteDialog(c.id)}><Delete/></IconButton>
              </Tooltip>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Paginazione */}
      <Stack direction="row" spacing={1} sx={{ mt:2 }} flexWrap="wrap">
        {Array.from({ length: totalPages }, (_, i)=>(
          <Button key={i} variant={i===page?'contained':'outlined'} size="small" onClick={()=>fetchContribuenti(i)}>{i+1}</Button>
        ))}
      </Stack>

      <ConfirmDeleteModal
        open={openDeleteModal}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
        message="Sei sicuro di voler eliminare questo contribuente?"
      />
    </Container>
  );
}
